import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Grid, TextField, 
  Button, FormControl, InputLabel, Select, MenuItem, 
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, TablePagination, Chip, IconButton, Divider,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Checkbox, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import FilterListIcon from '@mui/icons-material/FilterList';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import axios from 'axios';
import moment from 'moment';

const QAReview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [calls, setCalls] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCalls, setTotalCalls] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCalls, setSelectedCalls] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    tenantId: localStorage.getItem('tenantId') || '',
    startDate: null,
    endDate: null,
    disposition: '',
    agentId: '',
    status: 'pending,processing,completed,analyzed,transcribed,submitted',  // Include all statuses to show calls throughout their lifecycle
    reviewed: 'all'
  });
  

  
  // Load calls on initial render and when filters change
  useEffect(() => {
    fetchCalls();
  }, [page, rowsPerPage]);
  
  const fetchCalls = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = { 
        ...filters, 
        page: page + 1, 
        limit: rowsPerPage,
        startDate: filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : undefined,
        endDate: filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : undefined
      };
      
      const response = await axios.get('/api/qa/calls', { params });
      setCalls(response.data.data);
      setTotalCalls(response.data.count);
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  };
  

  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleFilterChange = (field) => (event) => {
    setFilters({
      ...filters,
      [field]: event.target.value
    });
  };
  
  const handleDateChange = (field) => (value) => {
    setFilters({
      ...filters,
      [field]: value ? new Date(value) : null
    });
  };
  
  const applyFilters = () => {
    setPage(0);
    fetchCalls();
    setFilterOpen(false);
  };
  
  const resetFilters = () => {
    setFilters({
      tenantId: localStorage.getItem('tenantId') || '',
      startDate: null,
      endDate: null,
      disposition: '',
      agentId: '',
      status: 'pending,processing,completed,analyzed,transcribed,submitted',  // Include all statuses to show calls throughout their lifecycle
      reviewed: 'all'
    });
  };
  
  const handleCallSelect = (callId) => {
    if (compareMode) {
      toggleCallSelection(callId);
    }
    // Note: Individual call review should be done via Call Analysis page
  };
  
  const toggleCallSelection = (callId) => {
    setSelectedCalls(prev => {
      if (prev.includes(callId)) {
        return prev.filter(id => id !== callId);
      } else {
        // Limit to max 4 calls
        if (prev.length >= 4) {
          return prev;
        }
        return [...prev, callId];
      }
    });
  };
  
  const toggleCompareMode = () => {
    setCompareMode(prev => !prev);
    if (!compareMode) {
      // Entering compare mode - clear selections
      setSelectedCalls([]);
    }
  };
  
  const startComparison = () => {
    if (selectedCalls.length < 2) {
      // Need at least 2 calls to compare
      return;
    }
    
    navigate(`/qa-comparative/${selectedCalls.join(',')}`);
  };
  

  
  // Render QA score chip with color based on score - Red, Yellow, Blue
  const renderScoreChip = (score) => {
    if (!score) return <Chip label="Not Reviewed" size="small" color="default" />;
    
    // Handle categorical scores
    if (typeof score === 'string') {
      switch (score.toLowerCase()) {
        case 'good':
          return <Chip label="Good" size="small" sx={{ backgroundColor: '#2196F3', color: 'white' }} />;
        case 'average':
          return <Chip label="Average" size="small" sx={{ backgroundColor: '#FFD700', color: 'black' }} />;
        case 'poor':
          return <Chip label="Poor" size="small" sx={{ backgroundColor: '#9E9E9E', color: 'white' }} />;
        case 'rejected':
          return <Chip label="Rejected" size="small" sx={{ backgroundColor: '#F44336', color: 'white' }} />;
        default:
          return <Chip label={score} size="small" color="default" />;
      }
    }
    
    // Handle numerical scores
    let customStyle = { backgroundColor: '#F44336', color: 'white' }; // Red
    if (score >= 80) customStyle = { backgroundColor: '#2196F3', color: 'white' }; // Blue
    else if (score >= 60) customStyle = { backgroundColor: '#FFD700', color: 'black' }; // Yellow
    
    return <Chip label={`${score}%`} size="small" sx={customStyle} />;
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4, mt: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          QA Review
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Compare multiple calls for quality analysis. Select 2-4 calls to perform comparative analysis and identify patterns.
        </Typography>
      </Box>
      
      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={() => setFilterOpen(true)}
              sx={{ mr: 1 }}
            >
              Filters
            </Button>
            
            <Button
              variant={compareMode ? "contained" : "outlined"}
              color={compareMode ? "secondary" : "primary"}
              startIcon={<CompareArrowsIcon />}
              onClick={toggleCompareMode}
            >
              {compareMode ? "Cancel Compare" : "Compare Calls"}
            </Button>
          </Box>
          
          <Box>
            {compareMode && selectedCalls.length >= 2 && (
              <Button 
                variant="contained" 
                color="secondary"
                onClick={startComparison}
                sx={{ mr: 1 }}
              >
                Compare {selectedCalls.length} Calls
              </Button>
            )}
            
            <Button 
              variant="contained" 
              onClick={fetchCalls}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        
        {compareMode && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: 1, borderColor: 'primary.200' }}>
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
              Comparative Analysis Mode
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select 2-4 calls to compare their QA scores, agent performance, and conversation patterns.
            </Typography>
            <Typography variant="body2" color="primary.main" fontWeight="medium">
              {selectedCalls.length} of 4 calls selected
            </Typography>
            
            {selectedCalls.length >= 2 && (
              <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                ✓ Ready for comparison! Click "Compare {selectedCalls.length} Calls" to proceed.
              </Typography>
            )}
            
            {selectedCalls.length === 4 && (
              <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                ⚠ Maximum of 4 calls reached. Deselect a call to select a different one.
              </Typography>
            )}
          </Box>
        )}
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {compareMode && <TableCell padding="checkbox"></TableCell>}
                <TableCell>Date/Time</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Disposition</TableCell>
                <TableCell>QA Score</TableCell>
                <TableCell>Transcription</TableCell>
                <TableCell>Analysis</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calls.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={compareMode ? 8 : 7} align="center">
                    No calls found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                calls.map((call) => (
                  <TableRow 
                    key={call.id} 
                    hover
                    selected={selectedCalls.includes(call.id)}
                    onClick={() => compareMode ? toggleCallSelection(call.id) : null}
                    sx={{ cursor: compareMode ? 'pointer' : 'default' }}
                  >
                    {compareMode && (
                      <TableCell padding="checkbox">
                        <Checkbox 
                          checked={selectedCalls.includes(call.id)}
                          disabled={selectedCalls.length >= 4 && !selectedCalls.includes(call.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleCallSelection(call.id);
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>{moment(call.callDate).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                    <TableCell>{call.phoneNumber}</TableCell>
                    <TableCell>{Math.floor(call.duration / 60)}:{String(call.duration % 60).padStart(2, '0')}</TableCell>
                    <TableCell>{call.agent?.name || 'Unknown'}</TableCell>
                    <TableCell>{call.disposition}</TableCell>
                    <TableCell>{renderScoreChip(call.qa_score)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={call.transcription_status || 'pending'} 
                        size="small" 
                        color={
                          call.transcription_status === 'completed' ? 'success' :
                          call.transcription_status === 'processing' ? 'warning' :
                          call.transcription_status === 'failed' ? 'error' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={call.analysis_status || 'pending'} 
                        size="small" 
                        color={
                          call.analysis_status === 'completed' ? 'success' :
                          call.analysis_status === 'processing' ? 'warning' :
                          call.analysis_status === 'failed' ? 'error' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                        {/* Individual call review is handled via Call Analysis page */}
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Use Call Analysis for individual review
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCalls}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Filter Dialog */}
      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Filter Calls</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                type="date"
                value={filters.startDate ? moment(filters.startDate).format('YYYY-MM-DD') : ''}
                onChange={(e) => handleDateChange('startDate')(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                type="date"
                value={filters.endDate ? moment(filters.endDate).format('YYYY-MM-DD') : ''}
                onChange={(e) => handleDateChange('endDate')(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Disposition</InputLabel>
                <Select
                  value={filters.disposition}
                  onChange={handleFilterChange('disposition')}
                  label="Disposition"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="SALE">SALE</MenuItem>
                  <MenuItem value="NO SALE">NO SALE</MenuItem>
                  <MenuItem value="CALLBACK">CALLBACK</MenuItem>
                  <MenuItem value="HANGUP">HANGUP</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Agent ID"
                value={filters.agentId}
                onChange={handleFilterChange('agentId')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                >
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="completed,analyzed">Completed & Analyzed</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Reviewed</InputLabel>
                <Select
                  value={filters.reviewed}
                  onChange={handleFilterChange('reviewed')}
                  label="Reviewed"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="reviewed">Reviewed</MenuItem>
                  <MenuItem value="unreviewed">Not Reviewed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetFilters}>Reset</Button>
          <Button onClick={() => setFilterOpen(false)}>Cancel</Button>
          <Button onClick={applyFilters} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>
      

    </Container>
  );
};

export default QAReview; 